/**
 * Citations in chat answers (ADMIN-8). The model cites web sources as [n]; the server
 * streams each source as a `source-url` part whose id is n. These helpers turn the [n]
 * markers into links the markdown renderer can show as chips.
 */

/** The href a citation marker becomes; `citationNumber` reads it back. */
const CITATION_HREF_PREFIX = "#cite-";

/** A [n] marker that isn't a link, a reference definition or escaped. */
const MARKER = /(?<![\\!\w])\[(\d{1,3})\](?![(:])/g;

/**
 * Rewrites [n] markers into markdown links (`[n](#cite-n)`), leaving fenced code
 * blocks and inline code untouched.
 */
export function linkCitations(markdown: string): string {
  if (!markdown.includes("[")) return markdown;
  // Split into code and non-code: fenced blocks (``` or ~~~, closed or still streaming)
  // and inline code spans stay as they are.
  const pieces = markdown.split(/(```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*`)/);
  return pieces
    .map((piece, i) =>
      i % 2 === 1 ? piece : piece.replace(MARKER, `[$1](${CITATION_HREF_PREFIX}$1)`),
    )
    .join("");
}

/** The citation number of a `#cite-n` href, or null for any other link. */
export function citationNumber(href: string | undefined): string | null {
  if (!href?.startsWith(CITATION_HREF_PREFIX)) return null;
  const n = href.slice(CITATION_HREF_PREFIX.length);
  return /^\d{1,3}$/.test(n) ? n : null;
}

/** "news.example.com" → "news.example.com" without "www.", or the raw string if not a URL. */
export function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * The site's own favicon. Loaded straight from the cited site (not a third-party
 * favicon service), since the user is about to open that site anyway.
 */
export function faviconUrl(url: string): string | null {
  try {
    const { protocol, host } = new URL(url);
    if (protocol !== "https:" && protocol !== "http:") return null;
    return `${protocol}//${host}/favicon.ico`;
  } catch {
    return null;
  }
}

/** A web source cited by a message: citation number, link and page title. */
export interface CitedSource {
  n: string;
  url: string;
  title: string;
}

/** The shape of a message part this module reads (AI SDK `source-url` → assistant-ui `source`). */
export type SourceLike = {
  type: string;
  sourceType?: string;
  id?: string;
  url?: string;
  title?: string;
};

/**
 * A message's web sources in citation order, one per citation number. Parts that aren't URL
 * sources are skipped; a source without a title falls back to its host.
 */
export function citedSources(parts: readonly SourceLike[]): CitedSource[] {
  const sources: CitedSource[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    if (part.type !== "source" || part.sourceType !== "url" || !part.url || !part.id) continue;
    if (seen.has(part.id)) continue;
    seen.add(part.id);
    sources.push({ n: part.id, url: part.url, title: part.title || hostLabel(part.url) });
  }
  return sources.sort((a, b) => Number(a.n) - Number(b.n));
}

/** "1 source", "8 sources". */
export function sourcesLabel(count: number): string {
  return `${count} ${count === 1 ? "source" : "sources"}`;
}

/**
 * The sources whose favicons stand for the whole list: the first few, one per site, so three
 * pages from the same site don't show the same icon three times.
 */
export function stackSources(sources: readonly CitedSource[], max = 3): CitedSource[] {
  const hosts = new Set<string>();
  const picked: CitedSource[] = [];
  for (const source of sources) {
    const host = hostLabel(source.url);
    if (hosts.has(host)) continue;
    hosts.add(host);
    picked.push(source);
    if (picked.length === max) break;
  }
  return picked;
}
